import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  archiveInteractionLog,
  getAssociatedRecords,
  getBusinessInteractionLog,
  getInteractionLog,
  getInteractionsLogs,
  updateInteractionLog
} from './api';
import { AssociatedRecordResponse, InteractionLogResponse } from './types';
import { queryClient } from 'providers/AllProviders';
import { useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useAgent } from 'providers/context/AgentProvider';

function useResolvedLocationAndAgentIds() {
  const { locationId: paramLocationId, agentId: paramAgentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();
  const { selectedLocation } = useBusiness();
  const { selectedAgentId } = useAgent();

  const locationId = paramLocationId ?? (selectedLocation != null ? String(selectedLocation) : undefined);
  const agentId = paramAgentId ?? (selectedAgentId != null ? String(selectedAgentId) : undefined);

  return { locationId, agentId };
}

export function useGetInteractionsLogs(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: InteractionLogResponse[]; meta: MetaApiResponse }, Error>
) {
  const { locationId, agentId } = useResolvedLocationAndAgentIds();

  return useQuery<{ data: InteractionLogResponse[]; meta: MetaApiResponse }>({
    queryKey: ['callLogs', locationId, agentId, params],
    queryFn: () => getInteractionsLogs(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}

export function useGetInteractionLog(
  logId?: string,
  params?: Record<string, any>,
  options?: Omit<UseQueryOptions<InteractionLogResponse, Error>, 'queryKey' | 'queryFn'> & {
    businessScoped?: boolean;
  }
) {
  const { locationId, agentId } = useResolvedLocationAndAgentIds();
  const { id: callLogId } = useParams<{ id: string }>();
  const id = logId ?? callLogId;
  const { businessScoped = false, ...queryOptions } = options ?? {};
  const useBusinessScope = businessScoped || (Boolean(id) && !(locationId && agentId));

  return useQuery<InteractionLogResponse>({
    queryKey: ['callLog', useBusinessScope ? 'business' : locationId, useBusinessScope ? null : agentId, id, params],
    queryFn: () =>
      useBusinessScope ? getBusinessInteractionLog(id!, params) : getInteractionLog(locationId!, agentId!, id!, params),
    enabled: Boolean(id && (useBusinessScope || (locationId && agentId))),
    ...queryOptions
  });
}

export function useArchiveInteractionLog() {
  const { locationId, agentId } = useResolvedLocationAndAgentIds();

  return useMutation({
    mutationFn: (id: number) => archiveInteractionLog(locationId!, agentId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['callLogs'],
        refetchType: 'active'
      });
    }
  });
}

export function useUpdateInteractionLog() {
  const { locationId, agentId } = useResolvedLocationAndAgentIds();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { interaction: { report_call: { description: string } } } }) =>
      updateInteractionLog(locationId!, agentId!, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callLogs'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['callLog'], refetchType: 'active' });
    }
  });
}

export function useGetAssociatedRecords(
  contactId?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ integration_entity_contacts: AssociatedRecordResponse[]; meta: MetaApiResponse }, Error>
) {
  const { selectedBusiness } = useBusiness();

  return useQuery<{ integration_entity_contacts: AssociatedRecordResponse[]; meta: MetaApiResponse }>({
    queryKey: ['associatedRecords', selectedBusiness, contactId, params],
    queryFn: () => getAssociatedRecords(contactId!, params),
    enabled: Boolean(selectedBusiness && contactId),
    ...options
  });
}
